import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ScrollText, ShieldCheck, Scale, History, Landmark, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/collection";

const REGISTERS = [
  { term: "Documented fact", desc: "A statement supported by a citable public source. Cited wherever possible." },
  { term: "Owner testimony", desc: "Firsthand history provided by the collection's owner, marked as such until independently documented." },
  { term: "Interpretation", desc: "Analysis drawn from evidence, always labelled as interpretation rather than fact." },
  { term: "Open question", desc: "Anything not yet proven or requiring further research, stated plainly." },
];

const PRINCIPLES = [
  { icon: ScrollText, title: "Evidence before assertion", body: "Every factual claim that can be cited should carry a citation. Where evidence is missing, the gap is marked, not filled with a guess." },
  { icon: Scale, title: "Documented, disputed, unknown", body: "Widely repeated figures — viewership, fundraising totals — are presented as claims with their sources, never restated as settled fact." },
  { icon: ShieldCheck, title: "Neutrality until verified", body: "Where the original artifact cannot yet confirm a detail (such as an approval date or a signature), it is held as verification-pending rather than decided." },
  { icon: History, title: "Corrections are part of the record", body: "When better evidence emerges, entries are corrected and the change is recorded. Studies carry a revision history and a status." },
];

export const AboutArchivePage = () => (
  <div className="bg-[#F5F4F0] text-[#1A1918]" data-testid="about-archive-page">
    <Helmet>
      <title>About the Archive | Elvis Aloha Collection</title>
      <meta name="description" content="Learn about the evidence-based research standards and governance behind the Elvis Aloha from Hawaii 1973 archive." />
      <link rel="canonical" href={`${window.location.origin}/about`} />
    </Helmet>
    <section className="border-b border-[#D6D3C8]">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <Reveal>
          <SectionLabel className="text-[#8C271E]">About This Archive</SectionLabel>
          <h1 className="showcase-serif font-light tracking-tight text-4xl sm:text-5xl mt-4 mb-6 leading-[1.05]">
            A Scholarly Archive for Elvis Presley's <span className="italic">Aloha from Hawaii</span>
          </h1>
          <p className="text-base md:text-lg text-[#5C5A56] leading-relaxed">
            This is a permanent, evidence-driven archive built around the most thoroughly documented surviving
            collection of <span className="italic">Aloha from Hawaii</span> ticket and production material. It is not a
            fan site or a collectibles gallery. Its purpose is to preserve these objects, document them in context, and
            build an authoritative historical record around them — a scholarly catalogue that can be cited by museums,
            historians, researchers, auction houses, and archivists.
          </p>
          <blockquote className="mt-8 border-l-2 border-[#8C271E] pl-6 py-1" data-testid="about-mission">
            <span className="showcase-label text-[#8C271E]">Mission</span>
            <p className="mt-2 showcase-serif text-xl md:text-2xl text-[#1A1918] leading-snug italic">
              The Elvis Aloha 1973 Archive exists to preserve, document, research, and interpret the history,
              production, provenance, and surviving artifacts of Elvis Presley's <span>Aloha from Hawaii</span> through
              original, evidence-based scholarship.
            </p>
            <p className="mt-3 text-sm text-[#5C5A56]">The collection is the foundation — not the entire purpose.</p>
          </blockquote>
        </Reveal>
      </div>
    </section>

    <section className="border-b border-[#D6D3C8]">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <Reveal>
          <SectionLabel className="text-[#8C271E]">Why It Exists</SectionLabel>
          <h2 className="showcase-serif text-3xl font-normal tracking-tight mt-4 mb-5">Documentation as preservation</h2>
          <div className="space-y-4 text-[15px] md:text-base text-[#5C5A56] leading-relaxed">
            <p>
              Original admission material and production artifacts from a landmark broadcast survive in limited numbers,
              and the knowledge surrounding them is easily lost. An object without its context is only an object; an
              object documented with its provenance, its production history, and its evidence becomes a historical record.
            </p>
            <p>
              This archive exists to keep that context attached to the objects — permanently, transparently, and in a
              form that future researchers can build upon.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="border-b border-[#D6D3C8] bg-[#EAE8E0]">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <Reveal>
          <SectionLabel className="text-[#8C271E]">How Evidence Is Handled</SectionLabel>
          <h2 className="showcase-serif text-3xl font-normal tracking-tight mt-4 mb-8">Four registers, kept separate</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-px bg-[#D6D3C8] border border-[#D6D3C8]">
          {REGISTERS.map((r) => (
            <div key={r.term} className="bg-[#F5F4F0] p-6">
              <h3 className="showcase-serif text-xl text-[#1A1918] mb-2">{r.term}</h3>
              <p className="text-sm text-[#5C5A56] leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-[#D6D3C8]">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <Reveal>
          <SectionLabel className="text-[#8C271E]">Principles</SectionLabel>
          <h2 className="showcase-serif text-3xl font-normal tracking-tight mt-4 mb-8">How the archive evaluates and corrects</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-8">
          {PRINCIPLES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex gap-4">
              <Icon className="w-6 h-6 text-[#8C271E] flex-shrink-0 mt-1" />
              <div>
                <h3 className="showcase-serif text-lg text-[#1A1918] mb-1">{title}</h3>
                <p className="text-sm text-[#5C5A56] leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section>
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <Reveal>
          <div className="flex items-center gap-3">
            <Landmark className="w-5 h-5 text-[#8C271E]" />
            <SectionLabel className="text-[#8C271E]">The Long-Term Goal</SectionLabel>
          </div>
          <h2 className="showcase-serif text-3xl font-normal tracking-tight mt-4 mb-5">Preserved for future generations</h2>
          <p className="text-[15px] md:text-base text-[#5C5A56] leading-relaxed mb-8 max-w-3xl">
            The aim is a durable, citable reference work that outlives any single owner or website — one that treats a
            concert ticket as what it truly is: primary evidence of a documented moment in cultural and broadcast
            history. The collection is the foundation; the scholarship built upon it is the contribution.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/studies" className="inline-flex items-center gap-2 bg-[#1A1918] text-white rounded-none px-7 py-3.5 text-sm tracking-wide hover:bg-[#8C271E] transition-colors" data-testid="about-studies-link">
              Read the Historical Studies <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/collection" className="inline-flex items-center gap-2 bg-transparent border border-[#1A1918] text-[#1A1918] rounded-none px-7 py-3.5 text-sm tracking-wide hover:bg-[#EAE8E0] transition-colors">
              Browse the Collection
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default AboutArchivePage;
